import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Send, Paperclip, Check } from 'lucide-react';
import { siteData } from '../data/mock';
import { useToast } from '../hooks/use-toast';

export default function Contact() {
  const { contact } = siteData;
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [files, setFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const handleFile = (e) => {
    const list = Array.from(e.target.files || []).map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
      file,
    }));
    setFiles((prev) => [...prev, ...list]);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.email) {
      toast({ title: 'Email required', description: 'Please provide an email address.' });
      return;
    }
    setSubmitted(true);
    toast({
      title: 'Message sent',
      description: `Thanks ${form.name || 'friend'} — we'll be in touch soon.`,
    });
    setForm({ name: '', email: '', message: '' });
    setFiles([]);
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <section id="contact" className="section relative overflow-hidden">
      <div className="absolute inset-0 bg-nebula" />
      <div className="absolute inset-0 grid-lines opacity-15" />

      <div className="container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="text-xs tracking-[0.4em] uppercase text-[#d5b56b] mb-6">
                — {contact.label}
              </div>
              <h2 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-[#eef1f8]">
                Let's build
                <br />
                <span className="italic text-gold">something</span>
                <br />
                <span className="text-[#eef1f8]/80">remarkable.</span>
              </h2>
              <p className="mt-8 text-lg leading-relaxed text-[#eef1f8]/70 max-w-md">
                {contact.description}
              </p>

              <div className="mt-12 space-y-6">
                {[
                  { Icon: Mail, label: 'Email', value: contact.email },
                  { Icon: Phone, label: 'Phone', value: contact.phone },
                  { Icon: MapPin, label: 'Location', value: contact.location },
                ].map(({ Icon, label, value }) => (
                  <div key={label} className="flex items-center gap-5">
                    <div className="w-12 h-12 rounded-full glass grid place-items-center">
                      <Icon size={18} className="text-[#d5b56b]" />
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#eef1f8]/40">{label}</div>
                      <div className="text-[#eef1f8] mt-1">{value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Form */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="glass rounded-3xl p-8 md:p-10 relative overflow-hidden"
          >
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#d5b56b]/10 blur-3xl" />
            <h3 className="font-serif text-3xl text-[#eef1f8] mb-8">Contact Us</h3>

            <div className="space-y-6">
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[#eef1f8]/50 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-b border-[#d5b56b]/20 py-3 text-[#eef1f8] outline-none focus:border-[#d5b56b] transition-colors"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[#eef1f8]/50 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-transparent border-b border-[#d5b56b]/20 py-3 text-[#eef1f8] outline-none focus:border-[#d5b56b] transition-colors"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[#eef1f8]/50 mb-2">
                  Message
                </label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-transparent border-b border-[#d5b56b]/20 py-3 text-[#eef1f8] outline-none focus:border-[#d5b56b] transition-colors resize-none"
                  placeholder="Tell us about your project…"
                />
              </div>

              {/* File attachment */}
              <div>
                <label className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-[#eef1f8]/60 cursor-pointer hover:text-[#d5b56b] transition-colors">
                  <Paperclip size={14} />
                  <span>Attach Files ({files.length})</span>
                  <input type="file" multiple hidden onChange={handleFile} />
                </label>
                {files.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {files.map(({ id, file }) => (
                      <span key={id} className="text-xs px-3 py-1 rounded-full bg-[#d5b56b]/10 border border-[#d5b56b]/20 text-[#eef1f8]/70">
                        {file.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <button type="submit" className="btn-primary w-full justify-center mt-4">
                {submitted ? (
                  <>
                    <Check size={16} /> Sent Successfully
                  </>
                ) : (
                  <>
                    <Send size={16} /> Send Message
                  </>
                )}
              </button>

              <p className="text-[11px] text-[#eef1f8]/40 leading-relaxed">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="#" className="text-[#d5b56b] hover:underline">Privacy Policy</a> and{' '}
                <a href="#" className="text-[#d5b56b] hover:underline">Terms of Service</a> apply.
              </p>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
